import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ResponseTime as ResponseTimeComponent } from "heat-next";

import { useFeatureToggles } from "@/context/feature-toggles";
import { useModal } from "@/context/modal/modal";
import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";
import FireMissionModalContent from "@/components/ui/modal-with-context/modal-contents/session-details-page/fire-mission/fire-mission-modal-content";

import base from "@/themes/base/base";

import { Features } from "@/enums/features";

import { useSessionInfo } from "../../hooks/useSessionInfo";

interface FireMission {
    name: string;
    x: string;
    y: string[];
}

const MODAL_HEIGHT_PERCENTAGE = 0.874;
const MODAL_NON_SCROLLABLE_CONTENT_HEIGHT = 198;

interface ResponseTimeProps {
    subDimensionData?: any;
    showYAxisLabel?: boolean;
    renderDivider?: boolean;
    hideOverflowY?: boolean;
    componentContainerGap?: string;
}

const ResponseTime: React.FC<ResponseTimeProps> = ({
    subDimensionData,
    showYAxisLabel = true,
    renderDivider,
    hideOverflowY,
    componentContainerGap,
}) => {
    const {
        combinedCustomChartsData,
        isFetchingCombinedCustomCharts,
        isErrorCombinedCustomCharts,
        fireMissionFilterOptions,
        setFireMissionFilterOptions,
        setSelectedFireMission,
        selectedFireMissionIndex,
        setSelectedFireMissionIndex,
        subDimensionsData: subDimensionsDataFromContext,
    } = useSessionDetailsContext();

    const { isFeatureActive } = useFeatureToggles();
    const isSymbolToggleActive = isFeatureActive(Features.SymbolToggle);

    const { openModal, setWidth, setHeight } = useModal();

    const { getSessionSubtitleDetails, logoUrl } = useSessionInfo();

    const [scrollableContainerHeight, setScrollableContainerHeight] = useState(
        () => window.innerHeight * MODAL_HEIGHT_PERCENTAGE - MODAL_NON_SCROLLABLE_CONTENT_HEIGHT,
    );

    const fireMissions: FireMission[] = useMemo(() => {
        return combinedCustomChartsData?.[0]?.rangeData?.[0]?.data;
    }, [combinedCustomChartsData]);

    const fireMissionsFilterOptions = useMemo(() => {
        return fireMissions?.reduce((acc: string[], mission: FireMission) => {
            if (!acc.includes(mission.x)) {
                acc.push(mission.x);
            }

            return acc;
        }, []);
    }, [fireMissions]);

    useEffect(() => {
        setFireMissionFilterOptions(fireMissionsFilterOptions);
    }, [fireMissionsFilterOptions]);

    //to update the ComponentsScrollableWrapper height, when screen height changes
    useEffect(() => {
        const handleScrollableContainerResize = () => {
            const updatedHeight =
                window.innerHeight * MODAL_HEIGHT_PERCENTAGE - MODAL_NON_SCROLLABLE_CONTENT_HEIGHT;
            setScrollableContainerHeight(updatedHeight);
        };

        window.addEventListener("resize", handleScrollableContainerResize);

        return () => window.removeEventListener("resize", handleScrollableContainerResize);
    }, []);

    //to update the DOM element's height, since styled components are not receiving the updated height
    useEffect(() => {
        const componentsScrollableWrapper = document.getElementById(
            "components-scrollable-wrapper",
        );

        if (componentsScrollableWrapper) {
            componentsScrollableWrapper.style.height = `${scrollableContainerHeight}px`;
        }
    }, [scrollableContainerHeight]);

    const handleModalOpen = useCallback(() => {
        setWidth("77.25682295%"); //for 1104px modal width, in breakpoint 1440px
        setHeight("87.4%");

        setSelectedFireMission(fireMissionFilterOptions && fireMissionFilterOptions[0]);
        setSelectedFireMissionIndex(0);

        openModal({
            content: (
                <FireMissionModalContent
                    scrollableContainerHeight={scrollableContainerHeight}
                    logoUrl={logoUrl}
                    getSessionSubtitleDetails={getSessionSubtitleDetails}
                    subDimensionsData={subDimensionsDataFromContext}
                />
            ),
            backgroundColor: `${base.colors.neutral.grey[900]}`,
            borderRadius: "0.25rem",
            padding: "1.5rem 1.5rem 0 1.5rem",
            boxShadow: "0px 0px 4px 1px rgba(255, 255, 255, 0.25)",
        });
    }, [
        fireMissionFilterOptions,
        scrollableContainerHeight,
        logoUrl,
        getSessionSubtitleDetails,
        selectedFireMissionIndex,
        subDimensionsDataFromContext,
    ]);

    const isEmptyState = () => {
        if (subDimensionData) {
            return false;
        } else if (
            !isErrorCombinedCustomCharts &&
            (!combinedCustomChartsData ||
                combinedCustomChartsData === null ||
                combinedCustomChartsData === undefined ||
                combinedCustomChartsData?.rangeData?.[0]?.data?.length === 0)
        ) {
            return true;
        }
    };

    const isErrorState =
        (!Array.isArray(combinedCustomChartsData) || isErrorCombinedCustomCharts) &&
        !isFetchingCombinedCustomCharts;

    if (isFetchingCombinedCustomCharts) {
        return <ContentLoader height="28.125rem" />;
    }

    if (isErrorState) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    description="error-state:description"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="25.125rem"
                />
            </ComponentWrapper>
        );
    }

    if (isEmptyState()) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-detail:response-time:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="25.125rem"
                />
            </ComponentWrapper>
        );
    }

    return (
        <ResponseTimeComponent
            data={subDimensionData ? subDimensionData : combinedCustomChartsData}
            onModalOpenClick={handleModalOpen}
            showYAxisLabel={showYAxisLabel}
            renderDivider={renderDivider}
            componentContainerGap={componentContainerGap}
            hideOverflowY={hideOverflowY}
            isSymbolToggleActive={isSymbolToggleActive}
        />
    );
};

export default ResponseTime;
